/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { fmtTime } from "./AgentCallsTable";

/* 经理人工评分：1–5 星 + 评语 */
export function RateCallDialog({ api, call, zh, onClose, onSaved }) {
  const [stars, setStars] = useState(call.managerRating?.stars || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(call.managerRating?.comment || "");
  const [saving, setSaving] = useState(false);
  const [self, setSelf] = useState(null);
  useEffect(() => { api.get("/telephony/rating-calibration/me", { params: { weeks: 4 } }).then(({ data }) => setSelf(data)).catch(() => setSelf(null)); }, [api]);
  const aiStars = typeof call.score === "number" ? Math.max(1, Math.min(5, Math.round(call.score / 20))) : null;
  const save = async () => {
    if (!stars) return toast.error(zh ? "请选择星级" : "Pick a star rating");
    setSaving(true);
    try {
      const { data } = await api.post(`/telephony/calls/${call.id}/rating`, { stars, comment });
      toast.success(zh ? `已评分 ${stars} ★` : `Rated ${stars} ★`);
      onSaved(data.rating); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSaving(false);
  };
  const remove = async () => {
    setSaving(true);
    try { await api.delete(`/telephony/calls/${call.id}/rating`); toast.success(zh ? "已撤销评分" : "Rating removed"); onSaved(null); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSaving(false);
  };
  const LABEL = zh ? ["", "很差", "较差", "一般", "良好", "优秀"] : ["", "Poor", "Fair", "OK", "Good", "Excellent"];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="rate-call-dialog">
        <DialogHeader>
          <DialogTitle>{zh ? "通话质量评价" : "Rate call quality"}</DialogTitle>
          <DialogDescription>{call.name || call.customerNumber} · {fmtTime(call.createdAt)}{typeof call.score === "number" ? ` · AI ${call.score}/100` : ""}</DialogDescription>
        </DialogHeader>
        <div className="flex-wrap md:flex-nowrap flex items-center gap-1 justify-center py-2" data-testid="rate-stars" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} type="button" data-testid={`rate-star-${i}`} onMouseEnter={() => setHover(i)} onClick={() => setStars(i)}
              className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`text-3xl leading-none transition-transform hover:scale-110 ${i <= (hover || stars) ? "text-amber-500" : "text-slate-200"}`)}>★</button>
          ))}
          <span className="ml-2 text-sm text-slate-500 w-14" data-testid="rate-label">{LABEL[hover || stars]}</span>
        </div>
        {(aiStars || self?.overall?.verdict) && (
          <p className="text-[11px] rounded-lg bg-sky-50 border border-sky-100 px-2.5 py-1.5 text-slate-600" data-testid="rate-self-hint">
            {aiStars && <span data-testid="rate-ai-stars">{zh ? `参考：本通 AI 话术评分 ${call.score} ≈ ${aiStars} ★` : `AI ${call.score} ≈ ${aiStars} ★`}</span>}
            {aiStars && self?.overall?.verdict && " · "}
            {self?.overall?.verdict && <span>{zh ? `您近 30 天评分偏差 ${self.overall.bias > 0 ? "+" : ""}${self.overall.bias}（${{ lenient: "偏松", strict: "偏严", aligned: "一致" }[self.overall.verdict]}）` : `Your 30d bias ${self.overall.bias > 0 ? "+" : ""}${self.overall.bias} (${self.overall.verdict})`}</span>}
          </p>
        )}
        <textarea data-testid="rate-comment" value={comment} onChange={(e) => setComment(e.target.value.slice(0, 500))} rows={3}
          placeholder={zh ? "评语（可选）：开场 / 需求挖掘 / 异议处理 / 促成…" : "Comment (optional)"} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" />
        {call.managerRating?.by && <p className="text-[11px] text-slate-400">{zh ? "上次评分：" : "Last: "}{call.managerRating.byName || call.managerRating.by} · {fmtTime(call.managerRating.at)}</p>}
        <div className="flex-wrap md:flex-nowrap flex items-center justify-between gap-2">
          {call.managerRating?.stars ? <Button type="button" variant="ghost" size="sm" data-testid="rate-remove" onClick={remove} disabled={saving} className="text-red-500">{zh ? "撤销评分" : "Remove"}</Button> : <span />}
          <div className="flex-wrap md:flex-nowrap flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} data-testid="rate-cancel">{zh ? "取消" : "Cancel"}</Button>
            <Button type="button" size="sm" data-testid="rate-save" onClick={save} disabled={saving} className="bg-brand-500 hover:bg-brand-600 text-white">{zh ? "保存评分" : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
